import { useState, useEffect, ChangeEvent } from "react";
import {
  Box,
  Grid,
  Tabs,
  Tab,
  Typography,
  useMediaQuery,
  Theme,
  styled,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ShowBase,
  useRecordContext,
  useDataProvider,
  ListButton,
} from "react-admin";
import { useCanAccess } from '@react-admin/ra-rbac';


import ComponentShow, { ShowActions } from "../../../components/PageComponents/ComponentShow";
import InfoOrderTab from "./InfoOrderTab";
import InfoCustomerTab from "./InfoCustomerTab";
import InfoPaymentTab from "./InfoPaymentTab";
import InfoConversionTab from "./InfoConversionTab";
import InfoDocumentTab from "./InfoDocumentTab";
import InfoKytTab from "./InfoKytTab";
import InfoAuditTab from "./InfoAuditTab";
import RecentActivity from "./RecentActivity";
import { formatCrypto, formatDateTime, formatEuro, networkLabel } from "../../../utils/paymentFormat";
import DetailHeader from "../../../components/PageComponents/DetailHeader";
import { StatusLabel } from "../../../components/Label/status";

const TabsWrapper = styled(Tabs)(() => ``);

const OrderShow = () => {
  return (
    <ShowBase>
      <Content />
    </ShowBase>
  );
};

const Content = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <ComponentShow
      header={
        <DetailHeader
          kind="Transaction"
          title={record.euroAmount !== undefined ? `${formatEuro(record.euroAmount)} · ${formatCrypto(record.amount)} ${String(record.currency || "").toUpperCase()}` : `${formatCrypto(record.amount)} ${String(record.currency || "").toUpperCase()}`}
          status={<StatusLabel />}
          facts={[record.internalRef && `Ref. ${record.internalRef}`, networkLabel(record.network), formatDateTime(record.dateCreated)]}
        />
      }
    >
      <ContentShow />
    </ComponentShow>
  );
};

const ContentShow = () => {
  const { t }: { t: any } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const record = useRecordContext();
  const dataProvider = useDataProvider();

  const [currentTab, setCurrentTab] = useState<string>("infoOrder");
  const [payments, setPayments] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [kyts, setKyts] = useState([]);
  const [audit, setAudit] = useState([]);
  const { isLoading, canAccess } = useCanAccess({ action: 'list', resource: 'documentOrder' });
  const { canAccess: canReadAudit } = useCanAccess({ action: 'read', resource: 'cryptoOrders.tab.audits' });


  useEffect(() => {
    const fetchData = async () => {
      if (record?.id) {
        const [paymentsRes, conversionsRes, documentsRes, kytsRes] = await Promise.all([
          dataProvider.getList("cryptoOrdersPayments", {
            filter: { id: record.id },
            pagination: { page: 1, perPage: 10 },
            sort: { field: "createdAt", order: "DESC" },
          }),
          dataProvider.getList("cryptoOrdersConversions", {
            filter: { id: record.id },
            pagination: { page: 1, perPage: 10 },
            sort: { field: "createdAt", order: "DESC" },
          }),
          canAccess ? dataProvider.getList("documentOrder", {
            filter: { id: record.id },
            pagination: { page: 1, perPage: 10 },
            sort: { field: "createdAt", order: "DESC" }
          }) : Promise.resolve({ data: [], total: 0 }),
          canAccess ? dataProvider.getList("cryptoOrdersKyts", {
            filter: { id: record.id },
            pagination: { page: 1, perPage: 10 },
            sort: { field: "createdAt", order: "DESC" }
          }) : Promise.resolve({ data: [], total: 0 }),
        ]);

        setPayments(paymentsRes.data);
        setConversions(conversionsRes.data);
        setDocuments(documentsRes.data);
        setKyts(kytsRes.data);
      }
    };

    fetchData();
  }, [dataProvider, record, canAccess]);

  // Fetched on its own: a failure here must not hide the other tabs.
  useEffect(() => {
    if (!record?.id || !canReadAudit) return;
    dataProvider.getList("cryptoOrdersAudit", {
      filter: { id: record.id },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "at", order: "ASC" },
    }).then((res) => setAudit(res.data), () => setAudit([]));
  }, [dataProvider, record, canReadAudit]);

  const tabs = [{ value: "infoOrder", label: t("Summary") }];
  if(record.customer && record.customer.type) {
    tabs.push({ value: "infoCustomer", label: t("Customer") });
  }
  if (payments.length > 0) {
    tabs.push({ value: "InfoPaymentTab", label: t("Payments") });
  }
  if (conversions.length > 0) {
    tabs.push({ value: "InfoConversionTab", label: t("Conversions") });
  }
  if (documents.length > 0) {
    tabs.push({ value: "InfoDocumentTab", label: t("Documents") });
  }
  if (kyts.length > 0) {
    tabs.push({ value: "InfoKytTab", label: t("KYT") });
  }
  if (audit.length > 0) {
    tabs.push({ value: "InfoAuditTab", label: t("Audit trail") });
  }

  const handleTabsChange = (_event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };

  // 📱 Mobile view
  if (isMobile) {
    return (
      <Box sx={{ px: 2, mt: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ShowActions resource="cryptoOrders" />
        </Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("Summary")}
        </Typography>
        <InfoOrderTab />
        {record.customer && record.customer.type && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              {t("Customer")}
            </Typography>
            <InfoCustomerTab />
          </>
        )}

        {payments.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              {t("Payments")}
            </Typography>
            <InfoPaymentTab payments={payments} />
          </>
        )}

        {conversions.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              {t("Conversions")}
            </Typography>
            <InfoConversionTab conversions={conversions} />
          </>
        )}
        {documents.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              {t("Documents")}
            </Typography>
            <InfoDocumentTab documents={documents} />
          </>
        )}
        {kyts.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              {t("KYT")}
            </Typography>
            <InfoKytTab kyts={kyts} />
          </>
        )}
        {audit.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              {t("Audit trail")}
            </Typography>
            <InfoAuditTab entries={audit} />
          </>
        )}
      </Box>
    );
  }

  // 🖥️ Desktop view
  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      <Grid container direction="row" justifyContent="center" spacing={4} sx={{ px: 4 }}>
        <Grid item xs={9} md={9}>
          <TabsWrapper
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </TabsWrapper>
        </Grid>
        <Grid item xs={3} md={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ShowActions resource="cryptoOrders" />
        </Grid>
        <Grid item xs={12} md={8}>
          {currentTab === "infoOrder" && <InfoOrderTab />}
          {currentTab === "infoCustomer" && <InfoCustomerTab />}
          {currentTab === "InfoPaymentTab" && <InfoPaymentTab payments={payments} />}
          {currentTab === "InfoConversionTab" && <InfoConversionTab conversions={conversions} />}
          {currentTab === "InfoDocumentTab" && <InfoDocumentTab documents={documents} />}
          {currentTab === "InfoKytTab" && <InfoKytTab kyts={kyts} />}
          {currentTab === "InfoAuditTab" && <InfoAuditTab entries={audit} />}
        </Grid>
        <Grid item xs={12} md={4}>
          <RecentActivity conversions={conversions} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderShow;
